import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { Book, EntityType, Me, RecapChapter } from "../api";
import { useAuth } from "../auth";
import { BookmarkRuler, useFlash } from "../bookmark";
import {
  BookInfo,
  ENTITY_TYPES,
  ErrorNote,
  FACT_TYPES,
  JobBadge,
  Spinner,
  relationLabel,
  useLoad,
} from "../ui";

type Tab = "recap" | EntityType | "events";

function storedBookmark(bookId: string): number | null {
  try {
    const v = localStorage.getItem(`fabula.bookmark.${bookId}`);
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
}

function storeBookmark(bookId: string, value: number) {
  try {
    localStorage.setItem(`fabula.bookmark.${bookId}`, String(value));
  } catch {
    /* per-viewer convenience only */
  }
}

export function BookPage({ me }: { me: Me }) {
  const { bookId = "" } = useParams();
  const { api } = useAuth();
  const book = useLoad(() => api.book(bookId), [api, bookId]);

  const processing = book.data?.job_status === "queued" || book.data?.job_status === "running";
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(book.reload, 5000);
    return () => clearInterval(t);
  }, [processing, book.reload]);

  if (book.error) return <ErrorNote error={book.error} />;
  if (!book.data) return <Spinner />;
  return <BookView book={book.data} isOwner={book.data.owner_id === me.id} />;
}

function BookView({ book, isOwner }: { book: Book; isOwner: boolean }) {
  const { api } = useAuth();
  const first = book.first_chapter_index;
  const last = book.last_chapter_index;
  const [bookmark, setBookmark] = useState<number>(() => {
    const stored = storedBookmark(book.id);
    const start = stored ?? first ?? 0;
    return first !== null && last !== null ? Math.min(Math.max(start, first), last) : start;
  });
  const [tab, setTab] = useState<Tab>("character");
  const [selected, setSelected] = useState<number | null>(null);
  const [dots, setDots] = useState<number[]>([]);
  const [lit, setLit] = useState<number | null>(null);
  const [gain, flash] = useFlash();

  useEffect(() => storeBookmark(book.id, bookmark), [book.id, bookmark]);

  const recap = useLoad(
    () => (last === null ? Promise.resolve([]) : api.recap(book.id, bookmark)),
    [api, book.id, bookmark, last],
  );
  const titles = useMemo(() => {
    const m = new Map<number, RecapChapter>();
    recap.data?.forEach((c) => m.set(c.chapter_index, c));
    return m;
  }, [recap.data]);

  const header = (
    <div className="page-head">
      <div>
        <h1>{book.title}</h1>
        <p className="muted">
          {book.authors.join(", ")}{" "}
          <JobBadge
            status={book.job_status}
            stage={book.job_stage}
            notBefore={book.job_not_before}
          />
        </p>
        <details className="book-info">
          <summary>Boekgegevens</summary>
          <BookInfo book={book} />
        </details>
      </div>
      <div className="row">
        {book.last_chapter_index !== null && (
          <>
            <a className="btn" href={`#/books/${book.id}/opzoeken`}>
              Snel opzoeken
            </a>
            <a className="btn btn-ghost" href={`#/books/${book.id}/wiki`}>
              Open wiki
            </a>
          </>
        )}
        {isOwner && (
          <a className="btn btn-ghost" href={`#/books/${book.id}/beheer`}>
            Beheren & controleren
          </a>
        )}
      </div>
    </div>
  );

  if (last === null || first === null) {
    return (
      <div className="stack">
        {header}
        {book.job_error ? (
          <ErrorNote error={book.job_error} />
        ) : (
          <p className="muted">Het boek wordt verwerkt. Dit duurt meestal een paar minuten.</p>
        )}
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    ...ENTITY_TYPES.map((t) => ({ id: t.type as Tab, label: t.label })),
    { id: "recap", label: "Samenvatting" },
    { id: "events", label: "Gebeurtenissen" },
  ];
  const isWeave = ENTITY_TYPES.some((t) => t.type === tab);

  return (
    <div className="stack">
      {header}
      {book.job_status === "budget_exceeded" && (
        <p className="note">
          Niet alle hoofdstukken zijn verwerkt (budgetlimiet bereikt). {book.job_error}
        </p>
      )}
      <BookmarkRuler
        first={first}
        last={last}
        value={bookmark}
        onChange={setBookmark}
        current={titles.get(bookmark)?.title}
        gain={gain}
      />

      <div className="tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`tab ${tab === t.id ? "tab-active" : ""}`}
            onClick={() => {
              setTab(t.id);
              setSelected(null);
              setDots([]);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "recap" && <Recap chapters={recap.data} error={recap.error} />}
      {isWeave && (
        <>
          <Weave
            bookId={book.id}
            first={first}
            last={last}
            bookmark={bookmark}
            type={tab as EntityType}
            selected={selected}
            dots={dots}
            lit={lit}
            onSelect={(id) => {
              setSelected(id);
              setDots([]);
            }}
            onGain={flash}
          />
          {selected !== null && (
            <EntityPanel
              bookId={book.id}
              entityId={selected}
              bookmark={bookmark}
              titles={titles}
              isOwner={isOwner}
              onSelect={(id) => {
                setSelected(id);
                setDots([]);
              }}
              onFacts={setDots}
              onLit={setLit}
            />
          )}
        </>
      )}
      {tab === "events" && <Events bookId={book.id} bookmark={bookmark} titles={titles} />}
    </div>
  );
}

export function ChapterTag({
  index,
  titles,
}: {
  index: number;
  titles: Map<number, RecapChapter>;
}) {
  const title = titles.get(index)?.title;
  return (
    <span className="chapter-tag" title={title}>
      H{index}
    </span>
  );
}

function Recap({ chapters, error }: { chapters: RecapChapter[] | null; error: string | null }) {
  if (error) return <ErrorNote error={error} />;
  if (!chapters) return <Spinner />;
  return (
    <ol className="recap">
      {[...chapters].reverse().map((c) => (
        <li key={c.chapter_index} className={c.kind !== "chapter" ? "recap-matter" : ""}>
          <h3>
            <span className="chapter-tag">H{c.chapter_index}</span> {c.title}
          </h3>
          {c.kind !== "chapter" ? (
            <p className="muted">
              {c.kind === "front_matter" ? "Voorwerk" : "Nawerk"} — niet samengevat, want dit
              kan spoilers bevatten.
            </p>
          ) : (
            <p>{c.summary ?? <span className="muted">Geen samenvatting.</span>}</p>
          )}
        </li>
      ))}
    </ol>
  );
}

const WEAVE_TOP = 40;

/**
 * Every entity of one type as a thread over the chapter axis: from the chapter where it first
 * appears to the last chapter it appears in, both as far as the bookmark allows. Right of the
 * bookmark the axis stays empty. Sorted by first appearance, so the order of introduction shows.
 */
function Weave(props: {
  bookId: string;
  first: number;
  last: number;
  bookmark: number;
  type: EntityType;
  selected: number | null;
  dots: number[];
  lit: number | null;
  onSelect: (id: number) => void;
  onGain: (msg: string | null) => void;
}) {
  const { api } = useAuth();
  const { first, last, bookmark, type, onGain } = props;
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [all, setAll] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const list = useLoad(
    () => api.entities(props.bookId, bookmark, debounced || undefined),
    [api, props.bookId, bookmark, debounced],
  );
  const ofType = useMemo(
    () => (list.data ?? []).filter((e) => e.entity_type === type),
    [list.data, type],
  );

  // Micro-feedback: how many names the last bookmark move revealed (or hid again).
  const seen = useRef<{ bookmark: number; count: number; type: EntityType } | null>(null);
  useEffect(() => {
    if (!list.data || debounced) return;
    const prev = seen.current;
    if (prev && prev.type === type && prev.bookmark !== bookmark) {
      const d = ofType.length - prev.count;
      onGain(d > 0 ? `+${d} ${d === 1 ? "naam" : "namen"}` : d < 0 ? `${d} ${d === -1 ? "naam" : "namen"}` : "geen nieuwe namen");
    }
    seen.current = { bookmark, count: ofType.length, type };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data]);

  const rows = useMemo(() => {
    const ranked = [...ofType].sort((a, b) => b.mention_count - a.mention_count);
    const pick = all || debounced ? ranked : ranked.slice(0, WEAVE_TOP);
    return pick.sort(
      (a, b) => a.first_seen_chapter - b.first_seen_chapter || a.name.localeCompare(b.name),
    );
  }, [ofType, all, debounced]);

  const count = last - first + 1;
  const x = (ch: number) => ((ch - first + 0.5) / count) * 100;
  const marks = [...new Set([first, ...[10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 120, 140, 160].filter((n) => n > first + 3 && n < last - 3), last])];
  const label = ENTITY_TYPES.find((t) => t.type === type)?.label.toLowerCase() ?? "";

  return (
    <div className="stack">
      <div className="weave-tools">
        <input
          type="search"
          placeholder="Zoek op naam…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Zoeken"
        />
        {ofType.length > WEAVE_TOP && !debounced && (
          <div className="seg" role="group" aria-label="Hoeveel">
            <button aria-pressed={!all} onClick={() => setAll(false)}>
              Belangrijkste {WEAVE_TOP}
            </button>
            <button aria-pressed={all} onClick={() => setAll(true)}>
              Alle {ofType.length}
            </button>
          </div>
        )}
        <span className="muted small">
          Een lijn loopt van de eerste tot de laatste keer dat iemand voorkomt, tot je boekenlegger.
        </span>
      </div>
      <ErrorNote error={list.error} />
      {list.loading && !list.data && <Spinner />}
      {list.data && rows.length === 0 && (
        <p className="muted">
          {debounced
            ? `Geen bekende naam "${debounced}" tot en met hoofdstuk ${bookmark}.`
            : `Nog geen ${label} tot en met hoofdstuk ${bookmark}.`}
        </p>
      )}
      {rows.length > 0 && (
        <div className="weave-scroll">
          <div className="weave">
            <div className="weave-row weave-axis" aria-hidden="true">
              <span />
              <span className="ticks">
                {marks.map((n) => (
                  <em key={n} style={{ left: `${x(n)}%`, transform: n === last ? "translateX(-100%)" : undefined }}>
                    {n}
                  </em>
                ))}
              </span>
            </div>
            {rows.map((e) => {
              const sel = e.entity_id === props.selected;
              const end = Math.min(e.last_seen_chapter, bookmark);
              return (
                <button
                  key={e.entity_id}
                  className={`weave-row thread ${type === "place" ? "thread-place" : ""} ${sel ? "thread-sel" : ""}`}
                  onClick={() => props.onSelect(e.entity_id)}
                  aria-pressed={sel}
                  title={e.aliases.length ? `Ook: ${e.aliases.join(", ")}` : undefined}
                >
                  <span className="thread-name">
                    {e.name}
                    <small>H{e.first_seen_chapter}</small>
                  </span>
                  <span className="thread-track">
                    <span
                      className="thread-line"
                      style={{ left: `${x(e.first_seen_chapter)}%`, width: `${x(end) - x(e.first_seen_chapter)}%` }}
                    />
                    {sel &&
                      props.dots.map((ch) => (
                        <span
                          key={ch}
                          className={`thread-dot ${props.lit === ch ? "lit" : ""}`}
                          style={{ left: `${x(ch)}%` }}
                        />
                      ))}
                  </span>
                </button>
              );
            })}
            <div className="weave-row" style={{ position: "absolute", inset: 0, height: "auto", pointerEvents: "none" }}>
              <span />
              <span style={{ position: "relative", height: "100%" }}>
                <span className="weave-now" style={{ left: `${((bookmark - first + 1) / count) * 100}%` }}>
                  <span>H{bookmark}</span>
                </span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EntityPanel(props: {
  bookId: string;
  entityId: number;
  bookmark: number;
  titles: Map<number, RecapChapter>;
  isOwner: boolean;
  onSelect: (id: number) => void;
  onFacts: (chapters: number[]) => void;
  onLit: (chapter: number | null) => void;
}) {
  const { api } = useAuth();
  const detail = useLoad(
    () => api.entity(props.bookId, props.entityId, props.bookmark),
    [api, props.bookId, props.entityId, props.bookmark],
  );
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const { onFacts } = props;
  useEffect(() => {
    // The dossier sits below the threads: bring it into view when someone is picked.
    ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [props.entityId]);
  useEffect(() => {
    onFacts(detail.data ? [...new Set(detail.data.facts.map((f) => f.chapter_index))] : []);
  }, [detail.data, onFacts]);

  if (detail.error)
    return (
      <section className="dossier" ref={ref}>
        <div className="dossier-meta" />
        {detail.error === "entity not found" ? (
          <p className="muted">Op dit punt in het boek nog niet bekend.</p>
        ) : (
          <ErrorNote error={detail.error} />
        )}
      </section>
    );
  if (!detail.data)
    return (
      <section className="dossier" ref={ref}>
        <div className="dossier-meta" />
        <Spinner />
      </section>
    );
  const e = detail.data;

  async function hideFact(factId: number) {
    setBusy(true);
    try {
      await api.editItem(props.bookId, "fact", factId, { status: "hidden" });
      detail.reload();
    } finally {
      setBusy(false);
    }
  }

  async function rename() {
    const name = window.prompt("Nieuwe naam", e.name);
    if (!name || name === e.name) return;
    try {
      await api.renameEntity(props.bookId, e.entity_id, name);
      detail.reload();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err));
    }
  }

  const type = { character: "Personage", place: "Plaats", object: "Voorwerp", group: "Groep" }[e.entity_type];
  return (
    <section className="dossier" ref={ref} aria-live="polite">
      <div className="dossier-meta">
        <span>{type}</span>
        <span>
          vanaf <ChapterTag index={e.first_seen_chapter} titles={props.titles} />
        </span>
        <span>
          laatst in <ChapterTag index={e.last_seen_chapter} titles={props.titles} />
        </span>
        <span>
          {e.facts.length} {e.facts.length === 1 ? "feit" : "feiten"}
        </span>
        {props.isOwner && (
          <span>
            <button className="link small" onClick={rename}>
              Naam wijzigen
            </button>
          </span>
        )}
      </div>
      <div className="dossier-main">
        <h2>{e.name}</h2>
        <p className="dossier-aka">{e.aliases.length > 0 ? `ook ${e.aliases.join(", ")}` : "\u00a0"}</p>
        {e.facts.length === 0 && <p className="muted">Nog geen feiten.</p>}
        <ol className="facts">
          {e.facts.map((f) => (
            <li
              key={f.fact_id}
              onMouseEnter={() => props.onLit(f.chapter_index)}
              onMouseLeave={() => props.onLit(null)}
            >
              <ChapterTag index={f.chapter_index} titles={props.titles} />
              <p>
                {f.text} <span className="fact-type">{FACT_TYPES[f.fact_type] ?? f.fact_type}</span>
              </p>
              {props.isOwner ? (
                <button
                  className="link small muted-link"
                  disabled={busy}
                  onClick={() => void hideFact(f.fact_id)}
                >
                  verbergen
                </button>
              ) : (
                <span />
              )}
            </li>
          ))}
        </ol>
      </div>
      <div className="dossier-side">
        <h3>Verbonden met</h3>
        {e.relations.length === 0 && <p>Nog geen relaties bekend.</p>}
        {e.relations.map((r) => (
          <p key={r.relation_id}>
            {relationLabel(r.relation_type, r.direction)}{" "}
            <button className="link" onClick={() => props.onSelect(r.other_entity_id)}>
              {r.other_name}
            </button>{" "}
            <ChapterTag index={r.chapter_index} titles={props.titles} />
          </p>
        ))}
      </div>
    </section>
  );
}

function Events(props: { bookId: string; bookmark: number; titles: Map<number, RecapChapter> }) {
  const { api } = useAuth();
  const events = useLoad(
    () => api.events(props.bookId, props.bookmark),
    [api, props.bookId, props.bookmark],
  );
  if (events.error) return <ErrorNote error={events.error} />;
  if (!events.data) return <Spinner />;
  if (events.data.length === 0) return <p className="muted">Nog geen gebeurtenissen.</p>;
  return (
    <ol className="timeline">
      {[...events.data].reverse().map((ev) => (
        <li key={ev.event_id}>
          <ChapterTag index={ev.chapter_index} titles={props.titles} /> <strong>{ev.title}</strong>
          <p>{ev.description}</p>
        </li>
      ))}
    </ol>
  );
}
